import { useSpeechToText, appendDictated } from '../../lib/useSpeechToText';

/**
 * Botón de micrófono que dicta texto dentro de un campo de nota.
 *
 * Se esconde solo si el navegador no implementa la Web Speech API (Firefox),
 * en vez de mostrar un botón que no haría nada.
 *
 * @param {(updater: (prev: string) => string) => void} onAppend
 *        Recibe una función actualizadora para no pisar lo que el usuario
 *        escriba a mano mientras dicta.
 */
export default function DictateButton({ onAppend, disabled = false, size = 20, className = '' }) {
    const { isSupported, isListening, interimText, error, toggle } = useSpeechToText({
        onResult: (text) => onAppend(prev => appendDictated(prev, text)),
    });

    if (!isSupported) return null;

    return (
        <div className="relative flex items-center">
            <button
                type="button"
                onClick={toggle}
                disabled={disabled}
                title={isListening ? 'Detener dictado' : 'Dictar nota por voz'}
                aria-label={isListening ? 'Detener dictado' : 'Dictar nota por voz'}
                aria-pressed={isListening}
                className={`relative transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    isListening
                        ? 'text-red-500 hover:text-red-600'
                        : 'text-gray-400 hover:text-primary'
                } ${className}`}
            >
                <span className="material-symbols-outlined" style={{ fontSize: size }}>
                    {isListening ? 'stop_circle' : 'mic'}
                </span>
                {isListening && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                    </span>
                )}
            </button>

            {/* Lo que se está reconociendo todavía, para saber que está escuchando */}
            {isListening && (
                <span className="ml-2 max-w-[220px] truncate text-[11px] italic text-gray-400">
                    {interimText || 'Escuchando...'}
                </span>
            )}

            {error && !isListening && (
                <span className="ml-2 max-w-[260px] text-[11px] text-red-500">{error}</span>
            )}
        </div>
    );
}
